import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { DollarSign } from 'lucide-react';
import PaymentModal from './PaymentModal';
import { toast } from '@/components/ui/use-toast';
import { formatDate } from '@/lib/dates';

interface UploadDriverJobsProps {
  driverName: string;
}

interface JobPaymentBreakdown {
  cash: number;
  eftpos: number;
  tm: number;
  tmHoist: number;
  acc: number;
  other: number;
  discount: number;
  eCard: number;
}

const UploadDriverJobs: React.FC<UploadDriverJobsProps> = ({ driverName }) => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [jobBreakdowns, setJobBreakdowns] = useState<Record<string, JobPaymentBreakdown>>({});
  const [jobNotes, setJobNotes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Format time as HH:MM
  const formatTime = (timeString?: string | null) => {
    if (!timeString) return 'N/A';
    return timeString.toString().slice(0, 5);
  };

  // Truncate address to everything before the first comma
  const formatLocation = (location?: string | null) => {
    if (!location) return 'N/A';
    const [firstPart] = location.split(',');
    return firstPart.trim();
  };

  useEffect(() => {
    const fetchJobs = async () => {
      if (!driverName) {
        console.warn('No driverName passed to UploadDriverJobs.');
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase
        .from('taxicaller_data')
        .select('*')
        .ilike('driver_name', driverName.trim())
        .or('driver_completed.is.null,driver_completed.eq.false');

      if (error) {
        console.error('Error fetching jobs:', error);
      } else {
        // Cancelled/no-show jobs never get closed out with a payment, so they
        // shouldn't sit in the driver's outstanding list even though
        // driver_completed is still false/null for them.
        const openJobs = (data || []).filter(
          (job) => job.Stage !== 'Cancelled' && job.Stage !== 'No show'
        );
        const sortedJobs = openJobs.sort((a, b) => {
          const keyA = `${a.pu_date || ''}T${a.pu_time || ''}`;
          const keyB = `${b.pu_date || ''}T${b.pu_time || ''}`;
          return keyA.localeCompare(keyB);
        });

        setJobs(sortedJobs);

        const breakdownInit: Record<string, JobPaymentBreakdown> = {};
        const notesInit: Record<string, string> = {};

        sortedJobs.forEach((job: any) => {
          breakdownInit[job.Job_ID] = {
            cash: 0,
            eftpos: 0,
            tm: 0,
            tmHoist: 0,
            acc: 0,
            other: 0,
            discount: 0,
            eCard: 0,
          };
          notesInit[job.Job_ID] = '';
        });

        setJobBreakdowns(breakdownInit);
        setJobNotes(notesInit);
      }

      setLoading(false);
    };

    fetchJobs();
  }, [driverName]);

  const handleJobBreakdownChange = (
    jobId: string,
    field: keyof JobPaymentBreakdown,
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setJobBreakdowns((prev) => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [field]: numValue,
      },
    }));
  };

  const handleJobNoteChange = (jobId: string, value: string) => {
    setJobNotes((prev) => ({
      ...prev,
      [jobId]: value,
    }));
  };

  const getJobBreakdownTotal = (jobId: string) => {
    const breakdown = jobBreakdowns[jobId];
    return Object.values(breakdown).reduce((sum, val) => sum + val, 0);
  };

  const handleOpenModal = (job: any) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleSaveRow = async (jobId: string) => {
    setIsSaving(true);
    try {
      const job = jobs.find((j) => j.Job_ID === jobId);
      if (!job) throw new Error('Job not found');

      // The list on screen can be stale: a job can be declined or re-assigned in TaxiCaller after it
      // loaded. Saving it then would record this driver's payment (and mark the job completed) against
      // a job that is now another driver's, hiding it from them. Re-check who owns it right now.
      const { data: current, error: currentError } = await supabase
        .from('taxicaller_data')
        .select('driver_name, Stage, driver_completed')
        .eq('Job_ID', jobId)
        .maybeSingle();
      if (currentError) throw currentError;
      const stillMine =
        !!current && (current.driver_name || '').trim().toLowerCase() === (driverName || '').trim().toLowerCase();
      if (!stillMine) {
        setJobs((prev) => prev.filter((j) => j.Job_ID !== jobId));
        toast({
          title: 'This job is no longer yours',
          description: current?.driver_name
            ? `It has been re-assigned to ${current.driver_name}, so it's been removed from your list. Nothing was saved.`
            : "It has been taken off you in TaxiCaller, so it's been removed from your list. Nothing was saved.",
          variant: 'destructive',
        });
        return;
      }

      const breakdown = jobBreakdowns[jobId];
      const note = jobNotes[jobId] || null;

      // Sanitize numeric fields
      const cash = parseFloat(breakdown.cash.toString()) || 0;
      const eftpos = parseFloat(breakdown.eftpos.toString()) || 0;
      const tm = parseFloat(breakdown.tm.toString()) || 0;
      const tmHoist = parseFloat(breakdown.tmHoist.toString()) || 0;
      const acc = parseFloat(breakdown.acc.toString()) || 0;
      const other = parseFloat(breakdown.other.toString()) || 0;
      const discount = parseFloat(breakdown.discount.toString()) || 0;
      const eCard = parseFloat(breakdown.eCard.toString()) || 0;

      const paidAmount = cash + eftpos + tm + tmHoist + acc + other + discount + eCard;

      const insertData = {
        taxicaller_job_id: jobId,
        driver_name: driverName,
        vehicle_number: job.vehicle_number,
        cash_amt: cash,
        eftpos_amt: eftpos,
        totalmobility_amt: tm,
        tm_hoist_amt: tmHoist,
        account_amt: acc,
        other_amt: other,
        discount: discount,
        ecard_amt: eCard,
        paid_amount: paidAmount,
        pu_date: job.pu_date,
        pu_time: job.pu_time,
        do_time: job.do_time,
        driver_notes: note,
        // Explicitly reset fields an upsert wouldn't otherwise touch, so
        // saving over a stale "Admin Tracking" exception placeholder (see
        // taxicaller_cancelled) doesn't inherit that row's Cost_Code,
        // ridewise_id, eft_id or reconciled/paid flags.
        Cost_Code: null,
        ridewise_id: null,
        eft_id: null,
        reconciled: false,
        driver_paid: false,
        tm_hoist_valid: false,
        total_mobility_valid: false,
        eftpos_valid: false,
        ecard_valid: false,
        updated_at: new Date().toISOString(),
      };

      // Upsert, not insert: a job that TaxiCaller briefly reported as
      // Cancelled/No show before reinstating it already has a placeholder
      // row here (auto-created under "Admin Tracking" by the taxicaller_cancelled
      // exception handler) -- the driver's real payment should replace that
      // placeholder rather than fail on the taxicaller_job_id unique constraint.
      const { error: insertError } = await supabase
        .from('driver_completed_payments')
        .upsert([insertData], { onConflict: 'taxicaller_job_id' });

      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from('taxicaller_data')
        .update({ driver_completed: true })
        .eq('Job_ID', jobId);

      if (updateError) throw updateError;

      setJobs((prev) => prev.filter((j) => j.Job_ID !== jobId));
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving row:', err);
      toast({
        title: 'Error',
        description: err?.message || 'Failed to save payment details.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading jobs for {driverName}...
      </div>
    );
  }

  return (
    <>
      <Card className="bg-card border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 golden-text">
            <DollarSign className="w-5 h-5" />
            Job by Job Payment Breakdown
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {jobs.length} jobs found for {driverName}. Tap on any row to enter payment details.
          </p>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center text-muted-foreground">
              No jobs to display.
            </div>
          ) : (
            <>
              <div>
                <Table>
                  <TableHeader>
                    <TableRow className="sticky top-0 bg-card z-10">
                      <TableHead className="text-xs w-20">Date</TableHead>
                      <TableHead className="text-xs w-16">Time</TableHead>
                      <TableHead className="text-xs">Passenger</TableHead>
                      <TableHead className="text-xs">Pickup</TableHead>
                      <TableHead className="text-xs">Drop-off</TableHead>
                      <TableHead className="text-xs w-20 text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow key={job.Job_ID}>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2 w-20 whitespace-nowrap"
                          onClick={() => handleOpenModal(job)}
                        >
                          {formatDate(job.pu_date)}
                        </TableCell>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2 w-16 whitespace-nowrap"
                          onClick={() => handleOpenModal(job)}
                        >
                          {formatTime(job.pu_time)}
                        </TableCell>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2"
                          onClick={() => handleOpenModal(job)}
                        >
                          {job.Passenger_First_Name || 'N/A'}
                        </TableCell>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2"
                          onClick={() => handleOpenModal(job)}
                        >
                          {formatLocation(job.Pick_Up_Location)}
                        </TableCell>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2"
                          onClick={() => handleOpenModal(job)}
                        >
                          {formatLocation(job.Drop_Off_Location)}
                        </TableCell>
                        <TableCell
                          className="cursor-pointer hover:bg-muted text-sm p-2 font-medium w-20 text-right"
                          onClick={() => handleOpenModal(job)}
                        >
                          ${(job.trip_total || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Separator className="my-4" />
              <div className="flex justify-between text-sm">
                <span>Total from Jobs:</span>
                <span className="font-medium">
                  ${jobs.reduce((sum, j) => sum + (j.trip_total || 0), 0).toFixed(2)}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {selectedJob && (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          job={selectedJob}
          breakdown={jobBreakdowns[selectedJob.Job_ID]}
          onBreakdownChange={(field, value) =>
            handleJobBreakdownChange(
              selectedJob.Job_ID,
              field as keyof JobPaymentBreakdown,
              value
            )
          }
          driverNote={jobNotes[selectedJob.Job_ID] || ''}
          onDriverNoteChange={(value) =>
            handleJobNoteChange(selectedJob.Job_ID, value)
          }
          onSave={() => handleSaveRow(selectedJob.Job_ID)}
          isSaving={isSaving}
        />
      )}
    </>
  );
};

export default UploadDriverJobs;
